import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { api, ApiError } from '../lib/api.js';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { ImageCapture } from '../components/ImageCapture.js';
import { useAuth } from '../lib/auth.js';
import { Pagination } from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';
import { TableLoader } from '../components/TableLoader';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly } from '../lib/dateUtils';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/BulkActionBar';

interface Personnel {
  id: string;
  full_name: string;
  designation: string;
  role?: 'trainer' | 'competent_person';
  qualification?: string;
  competency_cert_number?: string;
  competency_valid_from?: string;
  competency_valid_upto?: string;
  issuing_authority?: string;
  signature_image_url?: string | null;
  stamp_image_url?: string | null;
  competency_cert_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function PersonnelPage() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('personnel');

  const [items, setItems] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleTab, setRoleTab] = useState<'competent_person' | 'trainer' | 'all'>('competent_person');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('full_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'updated_at' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editing, setEditing] = useState<Personnel | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailPerson, setDetailPerson] = useState<Personnel | null>(null);
  const [certPreviewPerson, setCertPreviewPerson] = useState<Personnel | null>(null);
  const [certFileName, setCertFileName] = useState<string>('');
  const [success, setSuccess] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const {
    selectedIds,
    isSelected,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(items);

  const fields = useFormFields({
    full_name: '',
    designation: '',
    role: 'competent_person',
    qualification: '',
    competency_cert_number: '',
    competency_valid_from: '',
    competency_valid_upto: '',
    issuing_authority: '',
    signature_image_url: '',
    stamp_image_url: '',
    competency_cert_url: '',
  });

  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('personnel').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    return draftManager.subscribe(() => {
      setDraftCount(draftManager.listDrafts('personnel').length);
    });
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  const restorePersonnelDraft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditing(null);
    setModalError('');
    if (data && typeof data === 'object') {
      fields.patchValues(data as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restorePersonnelDraft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'personnel') {
        setIsResumed(true);
        restorePersonnelDraft(draft);
        const next = new URLSearchParams(searchParams);
        next.delete('resumeDraft');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, restorePersonnelDraft]);

  const isPersonnelDirty = useMemo(() => {
    if (!modalOpen || editing) return false;
    return Boolean(
      fields.values.full_name ||
      fields.values.qualification ||
      fields.values.competency_cert_number
    );
  }, [modalOpen, editing, fields.values]);

  const draftTitle = useMemo(() => {
    return fields.values.full_name ? `Personnel: ${fields.values.full_name}` : 'New Personnel';
  }, [fields.values.full_name]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'personnel',
    draftId: 'dft_personnel_new',
    enabled: modalOpen && !editing,
    isDirty: isPersonnelDirty,
    formData: fields.values,
    title: draftTitle,
    subtitle: fields.values.role === 'trainer' ? 'Trainer' : 'Competent Person',
    isResumed,
  });

  const load = () => {
    setLoading(true);
    const roleQuery = roleTab === 'all' ? undefined : roleTab;
    api.personnel.list({ search: search || undefined, role: roleQuery, page, limit: 20, sortBy, sortOrder } as Record<string, unknown>)
      .then((r) => {
        setItems(r.data as Personnel[] ?? []);
        setTotalPages((r as unknown as Record<string, number>).totalPages ?? 1);
        setTotal((r as unknown as Record<string, number>).total ?? 0);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, roleTab, sortBy, sortOrder]);
  useDebounce(search, () => { setPage(1); load(); });

  useEffect(() => {
    if (!detailPerson) return;
    const updated = items.find((p) => p.id === detailPerson.id);
    if (updated) setDetailPerson(updated);
  }, [items]);

  const openCreate = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditing(null);
    setModalError('');
    setExistingSignature(undefined);
    setExistingStamp(undefined);
    setCertFileName('');
    fields.reset({
      full_name: '',
      designation: roleTab === 'trainer' ? 'Trainer' : 'Competent Person',
      role: roleTab === 'all' ? 'competent_person' : roleTab,
      qualification: '',
      competency_cert_number: '',
      competency_valid_from: '',
      competency_valid_upto: '',
      issuing_authority: '',
      signature_image_url: '',
      stamp_image_url: '',
      competency_cert_url: '',
    });
    setModalOpen(true);
  };

  const openEdit = (p: Personnel) => {
    setIsResumed(false);
    setEditing(p);
    setModalError('');
    setCertFileName(p.competency_cert_url ? 'Certificate File Uploaded' : '');
    fields.patchValues({
      full_name: p.full_name,
      designation: p.designation ?? '',
      role: p.role ?? 'competent_person',
      qualification: p.qualification ?? '',
      competency_cert_number: p.competency_cert_number ?? '',
      competency_valid_from: p.competency_valid_from ?? '',
      competency_valid_upto: p.competency_valid_upto ?? '',
      issuing_authority: p.issuing_authority ?? '',
      signature_image_url: p.signature_image_url ?? '',
      stamp_image_url: p.stamp_image_url ?? '',
      competency_cert_url: p.competency_cert_url ?? '',
    });
    const ts = Date.now();
    setExistingSignature(p.signature_image_url ? `${api.personnel.imageUrl(p.id, 'signature')}?t=${ts}` : undefined);
    setExistingStamp(p.stamp_image_url ? `${api.personnel.imageUrl(p.id, 'stamp')}?t=${ts}` : undefined);
    setModalOpen(true);
  };

  const [existingSignature, setExistingSignature] = useState<string | undefined>();
  const [existingStamp, setExistingStamp] = useState<string | undefined>();

  const handleCertFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      fields.setField('competency_cert_url', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const isPersonnelFormValid = useMemo(() => {
    if (!fields.values.full_name.trim()) return false;
    if (fields.values.role !== 'trainer') {
      return (
        !!fields.values.competency_cert_number.trim() &&
        !!fields.values.competency_valid_from.trim() &&
        !!fields.values.competency_valid_upto.trim()
      );
    }
    return true;
  }, [fields.values]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');

    if (!fields.values.full_name.trim()) {
      setModalError('Full Name is required.');
      return;
    }

    if (fields.values.role !== 'trainer') {
      if (!fields.values.competency_cert_number.trim()) {
        setModalError('Competency Certificate Number is required for Competent Person.');
        return;
      }
      if (!fields.values.competency_valid_from.trim()) {
        setModalError('Licence Valid From date is required for Competent Person.');
        return;
      }
      if (!fields.values.competency_valid_upto.trim()) {
        setModalError('Licence Valid Upto date is required for Competent Person.');
        return;
      }
    }

    setSaving(true);
    const payload: Record<string, unknown> = {
      ...fields.values,
      designation: fields.values.designation.trim() || (fields.values.role === 'trainer' ? 'Trainer' : 'Competent Person'),
    };
    try {
      if (editing) await api.personnel.update(editing.id, payload);
      else await api.personnel.create(payload);
      clearDraft();
      setModalOpen(false);
      load();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.personnel.delete(deleteId);
      setSuccess('Personnel moved to Trash.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.personnel.bulkDelete(Array.from(selectedIds));
      setSuccess(res.data?.message || `Moved ${res.data?.count ?? selectedIds.size} personnel to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected personnel');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const isTrainerRole = fields.values.role === 'trainer';

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* Role Tabs & Top Bar */}
      <div className="shrink-0 space-y-3 pt-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Tab Pill Toggle */}
            <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
              <button
                type="button"
                onClick={() => setActiveTab('records')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === 'records'
                    ? 'bg-white text-gray-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Personnel Directory
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('drafts')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                  activeTab === 'drafts'
                    ? 'bg-white text-amber-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Drafts</span>
                {draftCount > 0 && (
                  <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                    {draftCount}
                  </span>
                )}
              </button>
            </div>

            {activeTab === 'records' && (
              <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
                <button
                  onClick={() => { setRoleTab('competent_person'); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                    roleTab === 'competent_person'
                      ? 'bg-white text-emerald-800 shadow-sm font-bold'
                      : 'text-slate-600 hover:bg-white/50'
                  }`}
                >
                  <svg className="w-3.5 h-3.5 text-emerald-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Competent Persons / Inspectors</span>
                </button>
                <button
                  onClick={() => { setRoleTab('trainer'); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                    roleTab === 'trainer'
                      ? 'bg-white text-brand-800 shadow-sm font-bold'
                      : 'text-slate-600 hover:bg-white/50'
                  }`}
                >
                  <svg className="w-3.5 h-3.5 text-brand-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 12v5c3 3 9 3 12 0v-5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Trainers / Faculty</span>
                </button>
                <button
                  onClick={() => { setRoleTab('all'); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    roleTab === 'all'
                      ? 'bg-white text-slate-900 shadow-sm font-bold'
                      : 'text-slate-600 hover:bg-white/50'
                  }`}
                >
                  All Personnel
                </button>
              </div>
            )}
          </div>
          {isWrite && <button onClick={openCreate} className="btn btn-primary">+ Add {roleTab === 'trainer' ? 'Trainer' : 'Competent Person'}</button>}
        </div>

        {activeTab === 'records' && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search name, qualification, cert..."
                className="input pl-9 text-sm w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button onClick={load} className="btn btn-secondary">Refresh</button>
          </div>
        )}

        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
          </div>
        )}
        {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      </div>

      {/* Table */}
      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="personnel"
            sectionTitle="Personnel"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <SortableHeader label="Name" field="full_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Category" field="role" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label={roleTab === 'trainer' ? 'Role' : 'Qualification / Designation'} field="designation" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Competency Cert No.</th>
                  <SortableHeader label="Licence Validity" field="competency_valid_upto" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-gray-500 py-8">No personnel found</td></tr>
                ) : items.map((p) => (
                  <tr
                    key={p.id}
                    className={`cursor-pointer hover:bg-brand-50/40 transition-colors ${isSelected(p.id) ? 'bg-brand-50/50 hover:bg-brand-50/70' : ''}`}
                    onClick={() => setDetailPerson(p)}
                  >
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected(p.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleSelect(p.id, e.shiftKey)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td className="font-medium text-brand-700 underline decoration-brand-300 hover:decoration-brand-700">{p.full_name}</td>
                    <td className="whitespace-nowrap">
                      {p.role === 'trainer' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200/80 shadow-2xs">
                          <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6 12v5c3 3 9 3 12 0v-5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>Trainer / Faculty</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80 shadow-2xs">
                          <svg className="w-3.5 h-3.5 text-emerald-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>Competent Person</span>
                        </span>
                      )}
                    </td>
                    <td className="text-gray-600 text-sm">
                      {p.qualification || p.designation || '—'}
                    </td>
                    <td className="text-gray-500 text-sm font-mono">{p.competency_cert_number || '—'}</td>
                    <td className="text-gray-500 text-sm">
                      {p.competency_valid_from && p.competency_valid_upto
                        ? `${formatDateOnly(p.competency_valid_from)} to ${formatDateOnly(p.competency_valid_upto)}`
                        : formatDateOnly(p.competency_valid_upto)}
                    </td>
                    <td className="whitespace-nowrap">
                      <TimestampCell createdAt={p.created_at} updatedAt={p.updated_at} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1.5 items-center">
                        <button
                          type="button"
                          onClick={() => setDetailPerson(p)}
                          title="View Personnel Details"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors cursor-pointer"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>View</span>
                        </button>
                        {p.role !== 'trainer' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (p.competency_cert_url) {
                                setCertPreviewPerson(p);
                              }
                            }}
                            disabled={!p.competency_cert_url}
                            title={p.competency_cert_url ? "View Competency Certificate PDF" : "No certificate uploaded"}
                            className={
                              p.competency_cert_url
                                ? "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200/80 transition-colors cursor-pointer"
                                : "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-400 border border-gray-200/80 opacity-60 cursor-not-allowed"
                            }
                          >
                            <svg className={`w-3.5 h-3.5 ${p.competency_cert_url ? 'text-indigo-600' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="8" r="6" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M15.477 12.89L17 22l-5-3l-5 3l1.523-9.11" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Cert</span>
                          </button>
                        )}
                        {isWrite && (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(p)}
                              title="Edit Personnel"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteId(p.id)}
                              title="Delete Personnel"
                              className="inline-flex items-center justify-center p-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsResumed(false);
          setEditing(null);
        }}
        title={editing ? 'Edit Personnel' : 'Add Personnel'}
        size="xl"
      >
        <DraftRecoveryBanner
          draft={existingDraft}
          onRestore={() => {
            const data = restoreDraft();
            if (data && existingDraft) {
              restorePersonnelDraft({ ...existingDraft, form_data: data });
            }
          }}
          onDiscard={discardDraft}
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {modalError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-700 flex items-center justify-between">
              <span>{modalError}</span>
              <button type="button" onClick={() => setModalError('')} className="text-red-500 hover:text-red-700">✕</button>
            </div>
          )}

          <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <span className="text-xs font-semibold text-gray-700 uppercase">Personnel Type:</span>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-800 cursor-pointer">
              <input
                type="radio"
                name="role"
                value="competent_person"
                checked={fields.values.role === 'competent_person'}
                onChange={() => {
                  fields.setField('role', 'competent_person');
                  if (!fields.values.designation || fields.values.designation === 'Trainer' || fields.values.designation === 'Trainer / Faculty') {
                    fields.setField('designation', 'Competent Person');
                  }
                }}
              />
              Competent Person
            </label>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-800 cursor-pointer">
              <input
                type="radio"
                name="role"
                value="trainer"
                checked={fields.values.role === 'trainer'}
                onChange={() => {
                  fields.setField('role', 'trainer');
                  if (!fields.values.designation || fields.values.designation === 'Competent Person') {
                    fields.setField('designation', 'Trainer');
                  }
                }}
              />
              Trainer / Faculty
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" required>
              <input className="input" {...fields.bind('full_name')} placeholder="e.g. Mr. Arpan Shah" required />
            </Field>
            {!isTrainerRole ? (
              <Field label="Qualification" required>
                <input className="input" {...fields.bind('qualification')} placeholder="e.g. M.TECH (PRODUCTION) BE (MECHANICAL)" required />
              </Field>
            ) : (
              <Field label="Designation (Optional)">
                <input className="input" {...fields.bind('designation')} placeholder="e.g. Trainer" />
              </Field>
            )}
          </div>

          {!isTrainerRole && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Competency Cert Number" required>
                  <input className="input" {...fields.bind('competency_cert_number')} placeholder="e.g. GUJ/DISH/CPTA/A-1401/2024" required />
                </Field>
                <Field label="Licence Valid From" required>
                  <input className="input" type="date" {...fields.bind('competency_valid_from')} required />
                </Field>
                <Field label="Licence Valid Upto" required>
                  <input className="input" type="date" {...fields.bind('competency_valid_upto')} required />
                </Field>
              </div>
              <Field label="Issuing Authority">
                <input className="input" {...fields.bind('issuing_authority')} placeholder="e.g. Director, Industrial Safety & Health, Gujarat State" />
              </Field>
              <Field label="Competency Certificate File (Optional - PDF or Image)">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleCertFileSelect}
                  className="input text-xs cursor-pointer py-1.5"
                />
                {certFileName && (
                  <p className="text-xs text-brand-700 font-medium mt-1">
                    Attached file: {certFileName}
                  </p>
                )}
              </Field>
            </>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
            <ImageCapture
              key="signature"
              label="Signature"
              value={existingSignature}
              onChange={(dataUrl) => fields.setField('signature_image_url', dataUrl)}
            />
            {!isTrainerRole && (
              <ImageCapture
                key="stamp"
                label="Stamp"
                value={existingStamp}
                hideDraw
                onChange={(dataUrl) => fields.setField('stamp_image_url', dataUrl)}
              />
            )}
          </div>

          {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setModalOpen(false);
                setIsResumed(false);
              }}
              className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0"
            >
              Cancel
            </button>
            <button type="submit" disabled={saving || !isPersonnelFormValid} className="btn btn-primary text-sm h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </Modal>

      {detailPerson && (
        <PersonnelDetail person={detailPerson} onClose={() => setDetailPerson(null)} />
      )}

      {certPreviewPerson && (
        <CertPreviewModal person={certPreviewPerson} onClose={() => setCertPreviewPerson(null)} />
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Move Personnel to Trash"
        message="Are you sure you want to delete this personnel record? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && activeTab === 'records' && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="personnel"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Personnel to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected personnel? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}

function PersonnelDetail({ person, onClose }: { person: Personnel; onClose: () => void }) {
  const { user } = useAuth();
  const [sigUrl, setSigUrl] = useState<string>('');
  const [stampUrl, setStampUrl] = useState<string>('');

  useEffect(() => {
    let sigObjectUrl = '';
    let stampObjectUrl = '';
    const fetchImage = async (kind: 'signature' | 'stamp', setter: (url: string) => void) => {
      const key = kind === 'signature' ? person.signature_image_url : person.stamp_image_url;
      if (!key) return;
      try {
        const token = await user?.getIdToken();
        const res = await fetch(`${api.personnel.imageUrl(person.id, kind)}?t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setter(url);
        if (kind === 'signature') sigObjectUrl = url;
        else stampObjectUrl = url;
      } catch { /* ignore */ }
    };
    fetchImage('signature', setSigUrl);
    fetchImage('stamp', setStampUrl);
    return () => {
      if (sigObjectUrl) URL.revokeObjectURL(sigObjectUrl);
      if (stampObjectUrl) URL.revokeObjectURL(stampObjectUrl);
    };
  }, [person.id, person.signature_image_url, person.stamp_image_url, user]);

  return (
    <Modal open={true} onClose={onClose} title={person.full_name} size="lg">
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow label="Full Name" value={person.full_name} />
          <DetailRow label="Role" value={person.role === 'trainer' ? 'Trainer / Faculty' : 'Competent Person'} />
          <DetailRow label="Qualification" value={person.qualification || person.designation} />
          <DetailRow label="Competency Cert No." value={person.competency_cert_number} />
          <DetailRow label="Licence Valid From" value={formatDateOnly(person.competency_valid_from)} />
          <DetailRow label="Licence Valid Upto" value={formatDateOnly(person.competency_valid_upto)} />
          <DetailRow label="Issuing Authority" value={person.issuing_authority} />
        </div>

        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">Signature & Stamp</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ImageSlot label="Signature" src={sigUrl} />
            <ImageSlot label="Stamp" src={stampUrl} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  );
}

function ImageSlot({ label, src }: { label: string; src: string }) {
  if (!src) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex items-center justify-center min-h-[100px]">
        <span className="text-sm text-gray-400">No {label.toLowerCase()}</span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 flex items-center justify-center min-h-[100px]">
      <img src={src} alt={label} className="max-h-28 object-contain" />
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <div><label className="label">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>{children}</div>;
}
function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between"><span>{message}</span><button onClick={onDismiss} className="text-red-500">✕</button></div>;
}

function CertPreviewModal({ person, onClose }: { person: Personnel; onClose: () => void }) {
  const { user } = useAuth();
  const [certUrl, setCertUrl] = useState<string>('');
  const [isPdf, setIsPdf] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<string>('');

  useEffect(() => {
    let objectUrl = '';
    const fetchCert = async () => {
      setLoading(true);
      try {
        const token = await user?.getIdToken();
        const res = await fetch(`${api.personnel.imageUrl(person.id, 'cert')}?t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load certificate file');
        const contentType = res.headers.get('content-type') || '';
        setIsPdf(contentType.includes('pdf') || person.competency_cert_url?.endsWith('.pdf') === true);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setCertUrl(objectUrl);
      } catch {
        setCertUrl('');
      } finally {
        setLoading(false);
      }
    };
    fetchCert();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [person.id, person.competency_cert_url, user]);

  const handleDownload = async () => {
    try {
      await api.personnel.downloadCertFile(person.id, `${person.full_name}_Competency_Certificate`);
    } catch {
      alert('Failed to download certificate');
    }
  };

  const handleShare = async () => {
    const shareableUrl = api.personnel.getShareableCertUrl(person.id);
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setToast('Shareable public certificate link copied to clipboard!');
      setTimeout(() => setToast(''), 4000);
    } catch {
      setToast(`Link: ${shareableUrl}`);
    }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Competency Certificate - ${person.full_name}`} size="xl">
      <div className="space-y-4">
        {toast && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {toast}
            </span>
            <button onClick={() => setToast('')} className="text-emerald-600 hover:text-emerald-900 font-bold">✕</button>
          </div>
        )}

        {/* Header Summary & Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
          <div>
            <p className="text-sm font-bold text-slate-800">{person.full_name}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">
              Cert No: {person.competency_cert_number || 'N/A'} {person.competency_valid_upto ? `| Valid Upto: ${formatDateOnly(person.competency_valid_upto)}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-xs cursor-pointer"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Download Cert</span>
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-xs cursor-pointer"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              <span>Share Link</span>
            </button>
          </div>
        </div>

        {/* Certificate File Preview Display */}
        <div className="rounded-xl border border-slate-200 bg-slate-100 min-h-[460px] flex items-center justify-center overflow-hidden">
          {loading ? (
            <div className="p-8 text-center space-y-2">
              <span className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-brand-600 border-t-transparent" />
              <p className="text-xs font-medium text-slate-600">Loading certificate preview...</p>
            </div>
          ) : certUrl ? (
            isPdf ? (
              <iframe src={certUrl} className="w-full h-[550px] border-0" title="Competency Certificate Preview" />
            ) : (
              <img src={certUrl} alt="Competency Certificate Preview" className="max-h-[550px] object-contain p-2" />
            )
          ) : (
            <div className="p-8 text-center text-slate-500">
              <p className="text-sm font-medium">Failed to load certificate preview.</p>
              <p className="text-xs mt-1">Please try downloading the certificate file directly using the download button above.</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
